import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IdCard, Plus, MoreHorizontal, Eye, Pencil, Trash2 } from 'lucide-react';
import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { useMyVCardList, deleteVCard, type VCard } from '@/api/account';
import { AccountAvatar } from './components/AccountAvatar';
import { vcardPublicUrl } from './components/VCardPreview';
import { VCardFormDrawer } from './components/VCardFormDrawer';
import { AccountListScaffold, type ScaffoldColumn } from './components/AccountListScaffold';

/** My VCards — manage (create / view / edit / delete) your digital contact cards. */
export default function VCardsPage() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading, isError, refetch } = useMyVCardList();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VCard | null>(null);
  const [deleting, setDeleting] = useState<VCard | null>(null);

  const del = useMutation({
    mutationFn: (id: number) => deleteVCard(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['account', 'vcards'] }); setDeleting(null); },
  });

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (v: VCard) => { setEditing(v); setFormOpen(true); };
  const view = (v: VCard) => window.open(vcardPublicUrl(v.uuid), '_blank', 'noopener,noreferrer');

  const columns = useMemo<ScaffoldColumn<VCard>[]>(() => [
    {
      id: 'name', header: 'Name',
      cell: ({ row }) => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
          <AccountAvatar name={row.original.name} src={row.original.avatar} size={28} />
          {row.original.name || '—'}
        </div>
      ),
    },
    { id: 'position', header: 'Position', accessorKey: 'position', cell: ({ getValue }) => String(getValue() ?? '—') },
    { id: 'email', header: 'Email', accessorKey: 'email', cell: ({ getValue }) => String(getValue() ?? '—') },
    { id: 'phone', header: 'Phone', accessorKey: 'phone', cell: ({ getValue }) => String(getValue() ?? '—') },
    {
      id: 'actions', header: '', enableSorting: false,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Actions"><MoreHorizontal size={16} /></IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => view(row.original)}><Eye size={16} /> View</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openEdit(row.original)}><Pencil size={16} /> Edit</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDeleting(row.original)}><Trash2 size={16} /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ], []);

  return (
    <>
      <AccountListScaffold
        title="My VCards"
        breadcrumbLabel="VCards"
        description="Manage your shareable digital contact cards"
        primaryAction={<Button startIcon={<Plus size={16} />} onClick={openNew}>New VCard</Button>}
        searchPlaceholder="Search VCards"
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        getRowId={(r) => String(r.id)}
        columns={columns}
        toggleableIds={['name', 'position', 'email', 'phone']}
        searchText={(r) => `${r.name} ${r.position} ${r.email} ${r.phone}`}
        filterFields={[
          { value: 'position', label: 'Position' },
          { value: 'email', label: 'Email' },
        ]}
        filterAccessor={(r, field) => (field === 'position' ? r.position : field === 'email' ? r.email : '')}
        emptyState={<EmptyState icon={<IdCard size={24} />} title="No VCards yet" description="Create your first digital contact card." />}
      />

      <VCardFormDrawer open={formOpen} onOpenChange={setFormOpen} editing={editing} />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete VCard?"
        description={deleting ? `"${deleting.name}" will be permanently removed.` : ''}
        destructive
        loading={del.isPending}
        confirmLabel="Delete"
        onConfirm={() => deleting && del.mutate(deleting.id)}
      />
    </>
  );
}
